import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid2,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { captureUTMParams, trackRegistration } from "../../utils/utmTracking";
import Styles from "../../styles/_global/Styles";
import OnBoarding_PersonalDetails from "../Forms/OnBoarding_PersonalDetails";
import OnBoarding_SecurityQuestions from "../Forms/OnBoarding_SecurityQuestions";
import OnBoarding_SMSLink from "../Forms/OnBoarding_SMSLink";
import OnBoarding_WhatsAppLink from "../Forms/OnBoarding_WhatsAppLink";

const steps = [
  {
    label: "Personal Details",
    description: "Enter your personal details to set up your account.",
  },
  {
    label: "Security Questions",
    description: "Please answer the following security questions.",
  },
  {
    label: "Verify Mobile Number",
    description: null,
  },
];

function OnBoarding() {
  const [activeStep, setActiveStep] = useState(0);
  const [personalDetails, setPersonalDetails] = useState({
    firstName: "",
    lastName: "",
    idNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    mobile: "",
  });
  const [disableContinue, setDisableContinue] = useState(false);
  const [disableFinish, setDisableFinish] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false); // Manage checkbox state

  // Define alertMessage and setAlertMessage
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("error");

  const [isMobileDevice, setIsMobileDevice] = useState(false);

  const securityQuestionsRef = useRef();
  const verificationCodeRef = useRef();

  useEffect(() => {
    // Detect if the user is on a mobile device
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent)) {
      setIsMobileDevice(true);
    }
    
    // Capture UTM parameters on component mount
    captureUTMParams();
  }, []);

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate Personal Details
      if (
        personalDetails.firstName &&
        personalDetails.lastName &&
        personalDetails.idNumber &&
        personalDetails.mobile &&
        personalDetails.email &&
        personalDetails.password &&
        personalDetails.password === personalDetails.confirmPassword
      ) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setDisableContinue(false); // Reset disable state when navigating forward
      }
    } else if (activeStep === 1) {
      const isValid = securityQuestionsRef.current.validate();
      if (isValid) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setDisableContinue(false); // Reset disable state when navigating forward
      }
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setDisableContinue(false); // Ensure Continue is not disabled after going back
  };

  const handleReset = () => {
    setActiveStep(0);
    setPersonalDetails({
      firstName: "",
      lastName: "",
      idNumber: "",
      email: "",
      password: "123456789@",
      confirmPassword: "123456789@",
      mobile: "",
    });
  };

  const handleFinish = async () => {
    setIsLoading(true);
    setDisableFinish(true); // Disable the button and show spinner
    setAlertMessage(""); // Clear any previous alert messages
    setAlertType(""); // Reset the alert type to prevent previous type from showing

    try {
      // Send the request to backend for user creation
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/user/create`,
        personalDetails, // Send the necessary user data
        {
          headers: {
            "x-api-key": process.env.REACT_APP_BACKEND_API_KEY,
          },
        }
      );

      // On success, set the response to localStorage
      localStorage.setItem("user", JSON.stringify(response.data));

      // Track successful registration with UTM parameters
      trackRegistration(personalDetails);

      // Handle the success (e.g., navigate to another page or show success message)
      console.log("User created successfully:", response.data);
      setAlertType("success");
      setAlertMessage("Please wait..."); // Success message
      // Redirect to sign in page
      window.location.href = "/signin";
    } catch (error) {
      console.error("Error creating user:", error);
      if (error.response && error.response.data && error.response.data.error) {
        setAlertType("error");
        setAlertMessage(error.response.data.error); // Show backend error message
      } else {
        setAlertType("error");
        setAlertMessage("Failed to create the user. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setDisableFinish(false); // Re-enable the Finish button
    }
  };

  const handleVerificationSuccess = () => {
    setActiveStep(steps.length); // Automatically go to the last step once verification is successful
  };

  const handleDisclaimerChange = (event) => {
    setIsDisclaimerChecked(event.target.checked);
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        minWidth: "100%",
        maxWidth: "100%",
        backgroundColor: "#f9f9f9",
        backgroundImage: 'url("/assets/bg-repeat-01.png")',
        backgroundRepeat: "repeat",
        pt: 4,
        pb: 4,
      }}
    >
      <img
        src="/assets/logo-dark.svg"
        alt="Logo"
        style={{ marginBottom: 20, width: 200 }}
      />

      <Paper
        elevation={3}
        sx={{
          padding: 4,
          borderRadius: 3,
          maxWidth: 500,
          boxShadow:
            "0px 4px 15px rgba(0, 0, 0, 0.05), 0px 2px 6px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box sx={{ width: "100%", mt: 1 }}>
          <Grid2
            container
            spacing={2}
            alignItems="center"
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Grid2 item>
              <Typography variant="h5" color="primary">
                Sign-Up
              </Typography>
            </Grid2>
            <Grid2 item sx={{ textAlign: "right" }}>
              <Typography variant="body2" color="textSecondary">
                Already have an account?
              </Typography>
              <Link
                to="/signin"
                variant="body2"
                sx={{
                  cursor: "pointer",
                  textDecoration: "none",
                  fontWeight: "500",
                  color: Styles.primary.main,
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                Sign In
              </Link>
            </Grid2>
          </Grid2>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  {step.description && (
                    <Typography sx={{ mb: 2 }}>{step.description}</Typography>
                  )}
                  {index === 0 && (
                    <OnBoarding_PersonalDetails
                      personalDetails={personalDetails}
                      setPersonalDetails={setPersonalDetails}
                      setDisableContinue={setDisableContinue}
                    />
                  )}
                  {index === 1 && (
                    <OnBoarding_SecurityQuestions
                      idNumber={personalDetails.idNumber}
                      ref={securityQuestionsRef}
                      setDisableContinue={setDisableContinue}
                    />
                  )}
                  {index === 2 &&
                    (isMobileDevice ? (
                      <OnBoarding_WhatsAppLink
                        mobile={personalDetails.mobile}
                        ref={verificationCodeRef}
                        onVerificationSuccess={handleVerificationSuccess}
                        setDisableContinue={setDisableContinue}
                      />
                    ) : (
                      <OnBoarding_SMSLink
                        mobile={personalDetails.mobile}
                        onVerificationSuccess={handleVerificationSuccess}
                      />
                    ))}
                  <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ color: "white" }}
                      disabled={disableContinue || index === 2} // Disable button when `disableContinue` is true
                    >
                      {index === steps.length ? "Finish" : "Continue"}
                    </Button>
                    {(disableContinue || activeStep > 0) && activeStep > 0 && (
                      <Button
                        variant="contained"
                        onClick={handleBack}
                        sx={{ color: "white" }}
                      >
                        Back
                      </Button>
                    )}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {activeStep === steps.length && (
            <Paper square elevation={0} sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ mt: -0.9 }}>
                  Verification Successful!
                </Typography>
                Almost there! Please read and agree to the disclaimer below to
                complete your sign-up.
              </Alert>

              <Box
                sx={{
                  mt: 2,
                  border: "1px solid #eee",
                  borderRadius: 3,
                  padding: 2,
                  maxHeight: 120,
                  overflowY: "auto",
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Sign-Up Disclaimer
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  When you sign up, you'll be automatically opted into WhatsApp
                  communication. We prefer chat communication to avoid
                  cluttering your email and SMS inboxes with more messages.
                  <br />
                  <br />
                  You can always opt out, but we hope you'll find our messages
                  valuable.
                  <br />
                  <br />
                  By signing up, you also agree to the terms and conditions and
                  privacy policy outlined in our{" "}
                  <Link
                    to="https://finatic.co.za/terms"
                    sx={{ color: "primary" }}
                    rel="noopener noreferrer"
                  >
                    Terms & Conditions
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="https://finatic.co.za/privacy"
                    sx={{ color: "primary" }}
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>{" "}
                  disclaimers.
                </Typography>
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDisclaimerChecked}
                    onChange={handleDisclaimerChange}
                    color="primary"
                  />
                }
                label="I have read and agree to the Disclaimer"
              />

              {alertMessage && (
                <Alert severity={alertType} sx={{ mt: 2, mb: 2 }}>
                  {alertMessage}
                </Alert>
              )}
              <Button
                onClick={handleFinish}
                sx={{ mt: 1, color: "white" }}
                variant="contained"
                disabled={isLoading || !isDisclaimerChecked} // Disable button unless checkbox is checked
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: "#fff" }} />
                ) : (
                  "Finish Sign-Up"
                )}
              </Button>
            </Paper>
          )}
        </Box>
      </Paper>
    </Container>
  );
}

export default OnBoarding;
